import { useNavigation } from '@react-navigation/core';
import { NavigationProp } from '@react-navigation/core/src/types';
import {
  NavigationContainer as _NavigationContainer,
  useIsFocused,
} from '@react-navigation/native';
import { ParamListBase } from '@react-navigation/routers/src/types';
import { StackNavigationProp } from '@react-navigation/stack';
import {
  ComponentProps,
  ComponentRef,
  createElement,
  forwardRef,
  Fragment,
  ReactElement,
  Ref,
  useEffect,
  useMemo,
  useRef,
} from 'react';
import {
  BehaviorSubject,
  combineLatest,
  combineLatestWith,
  delay,
  map,
  Observable,
  of,
} from 'rxjs';
import { distinctUntilChanged, switchMap } from 'rxjs/operators';
import { FCC } from '../../utils/react';
import { hasAny } from '../../utils/utils';
import {
  appState$,
  useIsAppActive,
  useIsAppActive$,
} from '../hooks/useAppState';
import { useCreation } from '../hooks/useCreation';
import { useCreateObservable } from '../rxReact/useCreateObservable';
import { RxScheduleRerenderProvider } from '../rxReact/useRxScheduleRerender';
import { PopNavigatorProvider, usePopNavigation } from './PopNavigatorProvider';

export function useUpdatingRefWhenVisible<T>(value: T) {
  const ref = useRef(value);
  const isFocused = useIsFocused();
  const isActive = useIsAppActive();
  useMemo(() => {
    if (isFocused && isActive) {
      ref.current = value;
    }
  }, [isActive, isFocused, value]);
  return ref.current;
}

export function useIsPageFocused$(
  passInNav?: NavigationProp<any>,
): BehaviorSubject<boolean> {
  const defaultNav = useNavigation();
  const behaviorSubject = useCreation(() => new BehaviorSubject(true), []);

  const nav = passInNav ?? defaultNav;
  useEffect(() => {
    const onFocused = () => behaviorSubject.next(true);
    const onBlurred = () => behaviorSubject.next(false);
    const focusSub = nav.addListener('focus', onFocused);
    const blurSub = nav.addListener('blur', onBlurred);
    return () => {
      focusSub();
      blurSub();
    };
  }, [behaviorSubject, nav]);

  return behaviorSubject;
}

export namespace useIsFocusedFn {
  export type OnChangeFn = (isFocusing: boolean) => void;

  export interface Callbacks {
    onChange?: OnChangeFn;
  }
}

export function useIsPageVisible$(): Observable<boolean> {
  const isAppActive$ = useIsAppActive$();

  const rootNav = usePopNavigation();
  const nav = useNavigation();

  const isRootFocused$ = useIsPageFocused$(rootNav as any);
  const isFocused$ = useIsPageFocused$(nav as any);

  return useCreation(
    () =>
      isAppActive$.pipe(
        combineLatestWith(isFocused$, isRootFocused$),
        map(
          ([isAppActive, isFocused, isRootFocused]) =>
            isAppActive && isFocused && isRootFocused,
        ),
      ),
    [isAppActive$, isFocused$, isRootFocused$],
  );
}

type NavigationStackPushArguments<
  P extends ParamListBase,
  RouteName extends Extract<keyof P, string>,
> = undefined extends P[RouteName]
  ? [screen: RouteName, params?: P[RouteName]]
  : [screen: RouteName, params: P[RouteName]];

interface LimitedNavigation<P extends ParamListBase> {
  push<RouteName extends Extract<keyof P, string>>(
    ...args: NavigationStackPushArguments<P, RouteName>
  ): void;
}
export type ResetStackFn = <P extends ParamListBase>(
  navigation: StackNavigationProp<P>,
  manipulator: (navigation: LimitedNavigation<P>) => void,
) => void;
export const useResetStack = (): ResetStackFn => {
  return (nav, manipulator) => {
    const routes: {
      name: string;
      params: object | undefined;
    }[] = [];

    const fakeNav: LimitedNavigation<ParamListBase> = {
      push(...args) {
        routes.push({ name: args[0], params: args[1] });
      },
    };

    manipulator(fakeNav);

    if (!hasAny(routes)) return;

    nav.reset({ index: routes.length - 1, routes: routes as any });
  };
};

export type CleanHistoryAndPushFn = <
  P extends ParamListBase,
  RouteName extends Extract<keyof P, string>,
>(
  navigation: StackNavigationProp<P>,
  ...args: NavigationStackPushArguments<P, RouteName>
) => void;
export const useCleanStackAndPush = (): CleanHistoryAndPushFn => {
  const resetStack = useResetStack();

  return <P extends ParamListBase, RouteName extends Extract<keyof P, string>>(
    nav: StackNavigationProp<P>,
    ...args: NavigationStackPushArguments<P, RouteName>
  ) => {
    resetStack<P>(nav, nav => {
      nav.push(...(args as any));
    });
  };
};

export const NavigationContainer: typeof _NavigationContainer = forwardRef(
  (
    props: Omit<ComponentProps<typeof _NavigationContainer>, 'ref'>,
    ref: Ref<ComponentRef<typeof _NavigationContainer>>,
  ): ReactElement => {
    const children = createElement(_NavigationContainer, {
      ...(props as any),
      ref,
    });

    if (props.independent) {
      // eslint-disable-next-line react/no-children-prop
      return createElement(PopNavigatorProvider, { children });
    } else {
      return createElement(Fragment, {}, children);
    }
  },
) as any;

export const RxAutoHangUpProviderForPage: FCC = props => {
  const isRxShouldHangUpForPage$ = useIsRxShouldHangUpForPage$();

  return createElement(
    RxScheduleRerenderProvider,
    { isShouldHangUpSignal$: isRxShouldHangUpForPage$ },
    props.children,
  );

  function useIsRxShouldHangUpForPage$(): Observable<boolean> {
    const isPageFocused$ = useIsPageFocused$();
    return useCreateObservable(
      inputs$ =>
        inputs$.pipe(
          switchMap(([isPageFocused$]) =>
            combineLatest([appState$, isPageFocused$]),
          ),
          map(
            ([appState, isPageFocused]) =>
              !isPageFocused || appState === 'background',
          ),
          distinctUntilChanged(),
          switchMap(isShouldHangUp =>
            isShouldHangUp ? of(true).pipe(delay(10 * 1000)) : of(false),
          ),
          distinctUntilChanged(),
        ),
      [],
      [isPageFocused$],
    );
  }
};
